/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState } from 'react';
import merge from 'lodash.merge';
import _ from 'lodash';
import AppStateContext from './appStateContext';
import useSessionState from "../../hooks/useSessionState";
import { appState as appStateDefault } from './defaultContext'

const AppStateProvider = ({ children }) => {
  const { setSessionStorageState, getSessionStorageState } = useSessionState();

  /**
   * App state / controls
   */
  const setAppStateDetails = ({
    currentPage,
    selectedContract,
    selectedCustomerId,
    sharepointLink,
    token,
    userId,
    name,
    customerInfoMap,
    userPermissionsList,
    contractCriticalCode,
    contractList,
    contractDepartments,
    contractCallers,
    actionCodes,
    siteLogo,
    contactEmail,
    serviceCentreCode,
    callTime,
    defaultNumber,
  }) => {
    updateAppStateDetails(prevState => {
      const newState = { ...prevState };

      const mergedState = merge(newState, {
        currentPage,
        selectedContract,
        selectedCustomerId,
        contactEmail,
        sharepointLink,
        token,
        userId,
        name,
        customerInfoMap,
        userPermissionsList,
        contractCriticalCode,
        contractList,
        contractDepartments,
        contractCallers,
        actionCodes,
        siteLogo,
        serviceCentreCode,
        callTime,
        defaultNumber,
      });

      setSessionStorageState(mergedState);
      return mergedState;
    });
  };

  /**
   * App page update
   */
  const setAppPageDetails = (currentPage: string) => {
    updateAppStateDetails(prevState => {
      const newState = { ...prevState };

      const mergedState = merge(newState, {
        currentPage,
      });

      setSessionStorageState(mergedState);

      return mergedState;
    });
  };

  /**
   * Delete state related to user
   */
  const deleteUserRelatedState = () => {
    updateAppStateDetails(prevState => {
      const newState = { ...prevState };

      const assignState = _.assign(newState, appStateDefault);

      setSessionStorageState(assignState);

      return assignState;
    });
  };

  const setContractCriticalCode = (criticalCodes: {
    CriticalCode: string,
    Details: string,
    PriorityCode: string,
    RespondTime1: number,
  }[]) => {
    updateAppStateDetails(prevState => {
      const newState = {...prevState};
      let criticalMapping = {};

      for(let i =0;i<criticalCodes.length;i++) {
        const criticalCode = criticalCodes[i].PriorityCode;

        if( !_.isNumber(criticalCode) && (criticalCode.toLowerCase().includes('pm') ||
          criticalCode.toLowerCase().includes('qu'))) {
          continue;
        }
        criticalMapping[criticalCode] = {};
        criticalMapping[criticalCode].CriticalCode = criticalCodes[i].CriticalCode;
        criticalMapping[criticalCode].Details = criticalCodes[i].Details;
        criticalMapping[criticalCode].PriorityCode = criticalCodes[i].PriorityCode;
        criticalMapping[criticalCode].ResponseTime = criticalCodes[i].RespondTime1;
      }
      const assignedState = _.assign(newState, {
        contractCriticalCode: criticalMapping,
      });

      return assignedState;
    })
  };

  const setContractCallers = (contracts: []) => {
    updateAppStateDetails(prevState => {
      const newState = {...prevState};
      let contractCallers = {};
      for(let i =0;i<contracts.length;i++) {
        let currentItems = contractCallers[contracts[i].ContractNo];
        let newItems;
        if(currentItems != undefined){
          newItems = _.union(currentItems,contracts[i].ContractCallers.ContractCaller);
        } else {
          if(Array.isArray(contracts[i].ContractCallers.ContractCaller)) {
            newItems = contracts[i].ContractCallers.ContractCaller;
          } else {
            newItems = [contracts[i].ContractCallers.ContractCaller];
          }
        }
        if(newItems[0] != undefined) contractCallers[contracts[i].ContractNo] = newItems;
      }
      const assignedState = _.assign(newState, {
        contractCallers: contractCallers,
      });
      setSessionStorageState(assignedState);
      return assignedState;
    })
  };

  const resetContractDetails = () => {
    updateAppStateDetails(prevState => {
      const newState = {...prevState};

      const mergeState = merge(newState, {
        contractCriticalCode: appStateDefault.contractCriticalCode,
        contractDepartments: appStateDefault.contractDepartments,
        contractCallers: appStateDefault.contractCallers,
      });
      return mergeState;
    })
  };

  const setContractList = (contractList: {}) => {
    updateAppStateDetails(prevState => {
      const newState = {...prevState};

      const assignState = _.assign(newState, {
        contractList: contractList,
      });
      return assignState;
    })
  };

  const updateCustomerInfoMap = (customerInfo: {
    BillTo: string,
    CustomerCode: string,
    CustomerName: string,
    TerritoryCode: number,
  }[]) => {
    updateAppStateDetails(prevState => {
      const newState = {...prevState};
      let customerInfoMap = _.cloneDeep(prevState.customerInfoMap);

      for(const customer of customerInfo) {
        let customerInfoMapElement = customerInfoMap[customer.CustomerCode];
        if (customerInfoMapElement != undefined) {
          customerInfoMapElement.CustomerName = customer.CustomerName;
        } else {
          customerInfoMapElement = {};
          customerInfoMapElement.CustomerName = customer.CustomerName;
          customerInfoMap[customer.CustomerCode] = customerInfoMapElement;
        }
      }

      return _.merge(newState, {
        customerInfoMap: customerInfoMap
      })
    })
  };

  // Initialise
  const localStorageUserState = getSessionStorageState();

  const appState = merge(
    localStorageUserState || {
      currentPage: 'home',
      token: '',
      userId: '',
      name: '',
      selectedContract: '',
      selectedCustomerId: '',
      customerInfoMap: {},
      userPermissionsList: [],
      contractList: {},
      actionCodes: [],
      siteLogo: {},
      defaultNumber: '',
      callTime: '',
      contractCriticalCode: {
        1: {
          CriticalCode: "PM1",
          Details: "Critical Risk",
          PriorityCode: 1,
        },
        2: {
          CriticalCode: "PM2",
          Details: "High Risk",
          PriorityCode: 2
        },
        3: {
          CriticalCode: "PM3",
          Details: "Medium Risk",
          PriorityCode: 3,
        },
        4: {
          CriticalCode: "PM4",
          Details: "Low Risk",
          PriorityCode: 4,
        },
      },
      contractDepartments: {},
      contractCallers: {},
      serviceCentreCode: '',
    },
    {
      deleteUserRelatedState,
      setAppPageDetails,
      setAppStateDetails,
      setContractCriticalCode,
      setContractCallers,
      setContractList,
      resetContractDetails,
      updateCustomerInfoMap,
    }
  );

  const [appStateDetails, updateAppStateDetails] = useState(appState);

  return <AppStateContext.Provider value={appStateDetails}>{children}</AppStateContext.Provider>;
};

export default AppStateProvider;
